import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Landmark
} from "lucide-react";
import * as ownerApi from "../../api/payroll/staffBankAccountApi";
import * as portalApi from "../../api/payroll/staffBankAccountPortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function StaffBankAccountList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/bank-accounts" : "/bank-accounts";
  const [list, setList] = useState([]);
  const toast = useToast();

  const load = async () => {
    try {
      const res = await api.getAllBankAccounts();
      setList(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this bank account?")) return;
    try {
      await api.deleteBankAccount(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = list.map((b) => ({ ...b, id: b._id }));

  const columns = [
    {
      key: "staff",
      header: "Staff",
      render: (b) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          {b.staffId?.name || "-"} ({b.staffId?.staffCode || ""})
        </span>
      ),
    },
    { key: "bankName", header: "Bank" },
    {
      key: "accountNumber",
      header: "Account",
      render: (b) => <span className="font-mono">{b.accountNumber}</span>,
    },
    {
      key: "ifscCode",
      header: "IFSC",
      render: (b) => <span className="font-mono">{b.ifscCode}</span>,
    },
    {
      key: "isDefault",
      header: "Default",
      render: (b) => <Badge variant={b.isDefault ? "primary" : "default"}>{b.isDefault ? "Yes" : "No"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (b) => (
        <div className="flex items-center justify-end gap-1">
          <Link to={`${base}/edit/${b._id}`}>
            <IconButton aria-label="Edit bank account" variant="ghost" size="sm">
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </Link>
          <IconButton
            aria-label="Deactivate bank account"
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(b._id)}
            className="hover:bg-danger-50 hover:text-danger-600"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          Staff Bank Accounts
        </h1>
        <Link to={`${base}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            emptyState={
              <EmptyState
                icon={Landmark}
                title="No bank accounts"
                description="Add a staff member's bank account for payroll disbursement."
                action={
                  <Link to={`${base}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}